import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TrendingDown, Loader2, RefreshCw, DollarSign, Clock, AlertTriangle, HandCoins, FileWarning, Settings, Users, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import LatePayRenters from "./LatePayRenters";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const STATUSES = ["Active Overdue", "Payment Promise Made", "Partially Paid", "Paid Late", "Returned Unpaid", "Vehicle Recovered", "Sent to Collections", "Payment Plan", "Waived", "Written Off", "Resolved"];
const VEHICLE_STATUSES = ["Renter Still Has Vehicle", "Vehicle Returned", "Vehicle Recovered", "Vehicle Out of Service", "Vehicle Available"];
const ADJ_TYPES = [["payment", "Payment received"], ["waive", "Rental charge waived"], ["late_fee_waive", "Late fee waived"], ["discount", "Discount given"], ["write_off", "Balance written off"], ["recovery_expense", "Recovery expense"], ["towing_expense", "Towing expense"], ["storage_expense", "Storage expense"], ["other_expense", "Other expense"], ["note", "Collection note"]];
const statusColor = (s) => s === "Active Overdue" ? "text-red-400 border-red-500/40 bg-red-500/[0.08]"
  : ["Paid Late", "Resolved"].includes(s) ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.08]"
  : ["Waived", "Written Off", "Returned Unpaid"].includes(s) ? "text-orange-300 border-orange-400/40 bg-orange-400/[0.08]"
  : "text-amber-300 border-amber-400/40 bg-amber-400/[0.08]";

const Card = ({ label, value, sub, danger }) => (
  <div className={`rounded-lg border p-3 ${danger ? "border-red-500/40 bg-red-500/[0.05]" : "border-white/10 bg-[#0C0C0C]"}`}>
    <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
    <div className={`font-display font-black text-xl mt-1 ${danger ? "text-red-400" : "text-white"}`}>{value}</div>
    {sub && <div className="text-[11px] text-white/40 mt-0.5">{sub}</div>}
  </div>
);

const inp = "w-full px-3 py-2 rounded-md bg-[#0A0A0A] border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]";

function AdjustForm({ rec, token, onSaved }) {
  const [f, setF] = useState({ type: "payment", amount: "", applies_to: "rental", reason: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const needsReason = ["waive", "write_off", "late_fee_waive", "discount"].includes(f.type);
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/leakage/${rec.id}/adjust`, { ...f, amount: Number(f.amount) || 0 },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Adjustment recorded — totals updated.");
      setF({ type: "payment", amount: "", applies_to: "rental", reason: "", notes: "" });
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save adjustment."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">
      <div className="text-xs font-bold text-[#F2B705] uppercase tracking-wide">Record Adjustment</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <select data-testid="adj-type" className={inp} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
          {ADJ_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input data-testid="adj-amount" className={inp} placeholder="Amount $" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
        <select data-testid="adj-applies" className={inp} value={f.applies_to} onChange={(e) => setF({ ...f, applies_to: e.target.value })}>
          <option value="rental">Rental charges</option><option value="late_fee">Late fees</option><option value="recovery">Recovery</option>
        </select>
        <input data-testid="adj-reason" className={inp} placeholder={needsReason ? "Reason (required)" : "Reason"} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
      </div>
      <div className="flex gap-2">
        <input className={inp} placeholder="Notes (optional)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        <button data-testid="adj-submit" onClick={submit} disabled={busy} className="shrink-0 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}

function PromiseForm({ rec, token, onSaved }) {
  const [f, setF] = useState({ amount: "", promised_date: "", promised_time: "5:00 PM", method: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.amount || !f.promised_date) { toast.error("Amount and date are required."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/leakage/${rec.id}/promise`, { ...f, amount: Number(f.amount) },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Payment promise recorded — it will auto-mark BROKEN if the time passes unpaid.");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save promise."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">
      <div className="text-xs font-bold text-[#F2B705] uppercase tracking-wide">Payment Promise</div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <input data-testid="promise-amount" className={inp} placeholder="Amount $" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
        <input data-testid="promise-date" type="date" className={inp} value={f.promised_date} onChange={(e) => setF({ ...f, promised_date: e.target.value })} />
        <input className={inp} placeholder="Time (5:00 PM)" value={f.promised_time} onChange={(e) => setF({ ...f, promised_time: e.target.value })} />
        <input className={inp} placeholder="Method" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} />
        <button data-testid="promise-submit" onClick={submit} disabled={busy} className="px-3 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold disabled:opacity-50">Save</button>
      </div>
    </div>
  );
}

function LateFeeSwitches({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => axios.get(`${API}/automations`, auth)
    .then((r) => {
      const map = {};
      (r.data.automations || []).forEach((a) => { map[a.key] = a; });
      setState(map);
    }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const toggle = async (key, label, enabled) => {
    let reason = "";
    if (!enabled) {
      reason = window.prompt(`Turn OFF ${label}?\nEnter a reason (required — kept in the audit log):`, "");
      if (reason === null) return;
      if (!reason.trim()) { toast.error("A reason is required to turn this off."); return; }
    }
    setBusy(key);
    try {
      await axios.patch(`${API}/automations/${key}`, { enabled, reason }, auth);
      toast.success(`${label} turned ${enabled ? "ON" : "OFF"}.${key === "late_fee_accrual" && !enabled ? " Fees are frozen — paused time is never billed." : ""}`, { duration: 6000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
    finally { setBusy(""); }
  };

  if (!state) return null;
  const rows = [
    ["late_fee_accrual", "Late Fees ($/hr charging)", "late-fees-switch"],
    ["late_fee_notifications", "Late-Fee Alerts (renter texts + reports)", "late-fee-alerts-switch"],
  ];
  return (
    <div data-testid="late-fee-switches" className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-white/10 bg-[#0C0C0C] px-4 py-2.5">
      {rows.map(([key, label, tid]) => {
        const on = !!state[key]?.effective;
        return (
          <label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
            <button data-testid={tid} disabled={busy === key} onClick={() => toggle(key, label, !on)}
              className={`relative w-10 h-6 rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-white/15"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
            </button>
            <span className={on ? "text-white/85" : "text-white/45"}>{label}</span>
            {!on && <span className="text-[10px] uppercase font-black px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/40">OFF</span>}
          </label>
        );
      })}
    </div>
  );
}

function ClearBalance({ rec, token, onSaved }) {
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState("collected");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const needReason = mode !== "collected";
  const MODES = [["collected", "Payment collected in full"], ["waive", "Waive / forgive balance"], ["write_off", "Write off (uncollectible)"]];
  const submit = async () => {
    if (needReason && !reason.trim()) { toast.error("A reason is required to waive or write off."); return; }
    if (!window.confirm(`Clear ${usd(rec.remaining_balance)} owed by ${rec.customer}? This closes the incident and is permanently logged in the audit trail.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/leakage/${rec.id}/clear`, { mode, reason },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Balance cleared — incident closed and extensions unlocked.");
      setShow(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not clear balance."); }
    finally { setBusy(false); }
  };
  if (rec.remaining_balance <= 0.01) return null;
  return (
    <div className="mt-3">
      {!show ? (
        <button data-testid={`clear-balance-${rec.id}`} onClick={() => setShow(true)}
          className="flex items-center gap-1.5 bg-emerald-500 text-black text-sm font-bold px-4 py-2 rounded-md hover:bg-emerald-400 transition-colors">
          <HandCoins size={15} /> Clear Balance ({usd(rec.remaining_balance)})
        </button>
      ) : (
        <div data-testid={`clear-balance-panel-${rec.id}`} className="rounded-lg border border-emerald-500/40 bg-emerald-500/[0.05] p-3 space-y-2">
          <div className="text-xs font-bold text-emerald-300 uppercase tracking-wide">Clear {usd(rec.remaining_balance)} — how should this be recorded?</div>
          <div className="flex flex-wrap gap-3">
            {MODES.map(([v, l]) => (
              <label key={v} className="flex items-center gap-1.5 text-sm text-white/80 cursor-pointer">
                <input type="radio" name={`clear-mode-${rec.id}`} data-testid={`clear-mode-${v}-${rec.id}`} checked={mode === v} onChange={() => setMode(v)} className="accent-emerald-400" /> {l}
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid={`clear-reason-${rec.id}`} className={inp} placeholder={needReason ? "Reason (required)" : "Reason (optional)"} value={reason} onChange={(e) => setReason(e.target.value)} />
            <button data-testid={`clear-confirm-${rec.id}`} onClick={submit} disabled={busy} className="shrink-0 px-4 py-2 rounded-md bg-emerald-500 text-black text-sm font-bold disabled:opacity-50">{busy ? "Clearing…" : "Confirm"}</button>
            <button onClick={() => setShow(false)} className="shrink-0 px-3 py-2 rounded-md border border-white/15 text-white/60 text-sm">Cancel</button>
          </div>
          <p className="text-white/40 text-[11px]">"Collected" marks it Paid Late · "Waive"/"Write off" count as lost revenue. Accrual stops, recovery tasks close, and extensions unlock either way.</p>
        </div>
      )}
    </div>
  );
}

export default function RevenueLeakage({ token, onAuthError }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("year");
  const [dates, setDates] = useState({ from: "", to: "" });
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [showCfg, setShowCfg] = useState(false);
  const [view, setView] = useState("incidents");
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { range, date_from: dates.from, date_to: dates.to, status, q, archived: showArchived };
      const [s, r] = await Promise.all([
        axios.get(`${API}/leakage/summary`, { ...auth, params: { range, date_from: dates.from, date_to: dates.to } }),
        axios.get(`${API}/leakage`, { ...auth, params }),
      ]);
      setSummary(s.data); setRecords(r.data);
    } catch (e) { if (e?.response?.status === 401) onAuthError?.(); else toast.error("Failed to load Revenue Leakage."); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, [range, dates, status, q, showArchived]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/leakage/settings`, auth).then((r) => setCfg(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);

  const saveCfg = async () => {
    try {
      const { data } = await axios.patch(`${API}/leakage/settings`, cfg, auth);
      setCfg(data); toast.success("Late-fee settings saved."); setShowCfg(false);
    } catch { toast.error("Could not save settings."); }
  };

  const setRecStatus = async (rec, field, value) => {
    try {
      await axios.post(`${API}/leakage/${rec.id}/status`, { [field]: value }, auth);
      toast.success("Updated."); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
  };

  const archiveRec = async (rec, flag) => {
    if (flag && !window.confirm(`Archive ${rec.customer}'s incident? It's hidden from the list and stats but can be restored anytime under "Archived".`)) return;
    try {
      await axios.post(`${API}/leakage/${rec.id}/archive`, { archived: flag }, auth);
      toast.success(flag ? "Incident archived." : "Incident restored.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); }
  };

  const deleteRec = async (rec) => {
    if (!window.confirm(`PERMANENTLY DELETE ${rec.customer}'s incident (${usd(rec.remaining_balance)} owed)?\n\nThis erases the record, its late fees, adjustments and history. This CANNOT be undone. Consider Archive instead.`)) return;
    try {
      await axios.delete(`${API}/leakage/${rec.id}`, auth);
      toast.success("Incident permanently deleted.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete."); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#F2B705]" size={24} /></div>;

  return (
    <div className="space-y-5" data-testid="revenue-leakage">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="font-display font-black text-2xl flex items-center gap-2"><TrendingDown className="text-red-400" size={22} /> Revenue Leakage</h2>
          <div className="flex rounded-md overflow-hidden border border-white/15">
            <button data-testid="leakage-view-incidents" onClick={() => setView("incidents")} className={`px-3 py-1.5 text-xs font-bold ${view === "incidents" ? "bg-[#F2B705] text-black" : "text-white/60"}`}>Incidents</button>
            <button data-testid="leakage-view-renters" onClick={() => setView("renters")} className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${view === "renters" ? "bg-[#F2B705] text-black" : "text-white/60"}`}><Users size={11} /> Late-Payment Renters</button>
          </div>
        </div>
        <div className="flex gap-2">
          <button data-testid="leakage-settings-btn" onClick={() => setShowCfg(!showCfg)} className="flex items-center gap-1.5 text-xs border border-white/15 text-white/70 px-3 py-1.5 rounded hover:bg-white/10"><Settings size={13} /> Late-Fee Settings</button>
          <button onClick={load} className="flex items-center gap-1.5 text-xs border border-white/15 text-white/70 px-3 py-1.5 rounded hover:bg-white/10"><RefreshCw size={13} /> Refresh</button>
        </div>
      </div>

      <LateFeeSwitches token={token} />

      {showCfg && cfg && (
        <div data-testid="leakage-settings" className="rounded-lg border border-[#F2B705]/40 bg-[#F2B705]/[0.05] p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <label className="text-xs text-white/60">Hourly late fee ($)<input className={inp + " mt-1"} value={cfg.hourly_late_fee} onChange={(e) => setCfg({ ...cfg, hourly_late_fee: Number(e.target.value) || 0 })} /></label>
          <label className="text-xs text-white/60">Grace period (minutes)<input className={inp + " mt-1"} value={cfg.grace_minutes} onChange={(e) => setCfg({ ...cfg, grace_minutes: Number(e.target.value) || 0 })} /></label>
          <label className="text-xs text-white/60">Late-fee cap per incident ($)<input data-testid="cfg-cap" className={inp + " mt-1"} value={cfg.late_fee_cap} onChange={(e) => setCfg({ ...cfg, late_fee_cap: Number(e.target.value) || 0 })} /></label>
          <button data-testid="cfg-save" onClick={saveCfg} className="px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold">Save Settings</button>
        </div>
      )}

      {view === "renters" && <LatePayRenters token={token} />}

      {/* Summary cards */}
      {view === "incidents" && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="leakage-cards">
          <Card label="Active Overdue Rentals" value={summary.active_overdue} danger={summary.active_overdue > 0} />
          <Card label="Currently Outstanding" value={usd(summary.outstanding)} danger={summary.outstanding > 0} />
          <Card label="Paid Late — This Month" value={usd(summary.paid_late_month)} sub="Delayed revenue (recovered)" />
          <Card label="Paid Late — This Year" value={usd(summary.paid_late_year)} sub="Delayed revenue (recovered)" />
          <Card label="Lost Revenue — Month" value={usd(summary.lost_month)} danger={summary.lost_month > 0} sub="Waived + written off" />
          <Card label="Lost Revenue — Year" value={usd(summary.lost_year)} danger={summary.lost_year > 0} sub="Waived + written off" />
          <Card label="Courtesy Days — Year" value={summary.courtesy_days_year} sub={`Value ${usd(summary.courtesy_value_year)} at risk`} />
          <Card label="Avg Days Late" value={summary.avg_days_late} sub={`${summary.incidents} incident(s)`} />
          <Card label="Late Fees Assessed" value={usd(summary.late_fees_assessed)} />
          <Card label="Late Fees Collected" value={usd(summary.late_fees_collected)} />
          <Card label="Late Fees Waived" value={usd(summary.late_fees_waived)} />
          <Card label="Late Fees Outstanding" value={usd(summary.late_fees_outstanding)} danger={summary.late_fees_outstanding > 0} />
        </div>
      )}

      {view === "incidents" && summary?.highest_risk_renters?.length > 0 && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/[0.04] p-4" data-testid="highest-risk">
          <div className="text-red-400 font-bold text-sm flex items-center gap-2"><AlertTriangle size={15} /> Highest-Risk Renters</div>
          <div className="mt-2 space-y-1">
            {summary.highest_risk_renters.map((r, i) => (
              <div key={i} className="flex flex-wrap justify-between text-sm text-white/70 border-b border-white/5 py-1">
                <span className="font-semibold text-white">{r.customer} <span className="text-white/40 font-normal">{r.phone}</span></span>
                <span>{r.incidents} incident(s) · {r.days_late}d late · <span className="text-red-400">{usd(r.outstanding)} owed</span>{r.lost > 0 && <span className="text-orange-300"> · {usd(r.lost)} lost</span>}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      {view === "incidents" && (
      <div className="flex flex-wrap gap-2 items-center">
        <select data-testid="leakage-range" className={inp + " !w-auto"} value={range} onChange={(e) => setRange(e.target.value)}>
          <option value="today">Today</option><option value="week">This Week</option><option value="month">This Month</option>
          <option value="quarter">This Quarter</option><option value="year">This Year</option><option value="custom">Custom Range</option>
        </select>
        {range === "custom" && (<>
          <input type="date" className={inp + " !w-auto"} value={dates.from} onChange={(e) => setDates({ ...dates, from: e.target.value })} />
          <input type="date" className={inp + " !w-auto"} value={dates.to} onChange={(e) => setDates({ ...dates, to: e.target.value })} />
        </>)}
        <select data-testid="leakage-status-filter" className={inp + " !w-auto"} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <input data-testid="leakage-search" className={inp + " !w-52"} placeholder="Search renter / phone / email" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="flex items-center gap-1.5 text-xs text-white/60 cursor-pointer ml-1">
          <input data-testid="leakage-show-archived" type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-[#F2B705]" />
          <Archive size={12} /> Archived
        </label>
      </div>
      )}

      {/* Records */}
      {view === "incidents" && (
      <div className="space-y-3" data-testid="leakage-records">
        {records.length === 0 && <div className="text-center py-10 border border-white/10 rounded-xl bg-[#0A0A0A] text-white/50">No revenue leakage records — everyone is paid up. 🎉</div>}
        {records.map((r) => (
          <div key={r.id} data-testid={`leakage-row-${r.id}`} className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4">
            <div className="flex flex-wrap justify-between gap-2 cursor-pointer" onClick={() => setOpen(open === r.id ? null : r.id)}>
              <div>
                <span className="font-display font-bold">{r.customer}</span>
                <span className="ml-2 text-[#F2B705] text-sm">{r.vehicle}</span>
                <span className={`ml-2 text-[11px] border rounded px-2 py-0.5 ${statusColor(r.status)}`}>{r.status}</span>
                <span className="ml-2 text-[11px] text-white/40">{r.vehicle_status}</span>
              </div>
              <div className="text-sm text-right flex items-center gap-2">
                <span className="text-red-400 font-bold">{usd(r.remaining_balance)} owed</span>
                <span className="text-white/40">{r.unpaid_days}d / {r.unpaid_hours}h late</span>
                {showArchived ? (
                  <button data-testid={`leakage-restore-${r.id}`} title="Restore from archive"
                    onClick={(e) => { e.stopPropagation(); archiveRec(r, false); }}
                    className="text-white/40 hover:text-emerald-400 transition-colors p-1"><ArchiveRestore size={15} /></button>
                ) : (
                  <button data-testid={`leakage-archive-${r.id}`} title="Archive (hide, keep history)"
                    onClick={(e) => { e.stopPropagation(); archiveRec(r, true); }}
                    className="text-white/40 hover:text-[#F2B705] transition-colors p-1"><Archive size={15} /></button>
                )}
                <button data-testid={`leakage-delete-${r.id}`} title="Delete permanently"
                  onClick={(e) => { e.stopPropagation(); deleteRec(r); }}
                  className="text-white/40 hover:text-red-400 transition-colors p-1"><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="mt-1 text-xs text-white/45 flex flex-wrap gap-x-4">
              <span>Paid through {fmtDateTime(r.paid_through)}</span>
              <span>📞 {r.phone}</span>
              <span>Courtesy: {r.courtesy_days}d = {usd(r.courtesy_value)}</span>
              <span>Delayed: {usd(r.delayed_revenue)}</span>
              <span className={r.lost_revenue > 0 ? "text-orange-300" : ""}>Lost: {usd(r.lost_revenue)}</span>
            </div>
            {open === r.id && (
              <div className="mt-3 border-t border-white/10 pt-3">
                {(r.recovery_required || r.alerts?.enforce_24h) && (
                  <div data-testid={`enforce-banner-${r.id}`} className="mb-3 rounded-lg border border-red-500/60 bg-red-500/10 p-3 text-xs">
                    <div className="text-red-300 font-bold flex items-center gap-1.5"><AlertTriangle size={13} /> 24-HOUR NONPAYMENT RULE {r.recovery_required ? "ACTIVE — Immediate Recovery Required" : "TRIGGERED"}</div>
                    <div className="text-white/60 mt-1">Coordinator Return for Nonpayment task created · renter notified · extensions locked.
                      {r.override_24h && <span className="text-amber-300"> Manager override by {r.override_24h.by} until {fmtDateTime(r.override_24h.until)} — {r.override_24h.reason}</span>}
                    </div>
                    {r.recovery_required && (
                      <button data-testid={`override-24h-${r.id}`} onClick={async () => {
                        const reason = window.prompt("MANAGER OVERRIDE — reason for pausing the 24-hour return rule (required):");
                        if (!reason?.trim()) return;
                        const hrs = Number(window.prompt("Extension duration in hours (1-168):", "24")) || 24;
                        if (!window.confirm("RISK ACKNOWLEDGMENT: I understand pausing mandatory recovery places revenue at risk, and this override is permanently logged. Proceed?")) return;
                        try {
                          await axios.post(`${API}/leakage/${r.id}/override-24h`, { reason, extension_hours: hrs, risk_acknowledged: true }, auth);
                          toast.success(`Override logged — rule paused ${hrs}h, extensions unlocked.`);
                          load();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Override failed."); }
                      }} className="mt-2 px-3 py-1.5 rounded border border-amber-400 text-amber-300 font-bold hover:bg-amber-400 hover:text-black transition-colors">Manager Override…</button>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  {[["Rental owed", usd(r.rental_owed)], ["Late fees assessed", usd(r.late_fees_assessed)],
                    ["Late fees collected", usd(r.collected_late_fees)], ["Late fees waived", usd(r.late_fees_waived)],
                    ["Recovery expenses", usd(r.recovery_expenses)], ["Collected (total)", usd(r.collected)],
                    ["Waived / discounts", usd(r.waived)], ["Written off", usd(r.written_off)],
                    ["Daily rate", usd(r.daily_rate)], ["Hourly fee", usd(r.hourly_late_fee)],
                    ["Total owed", usd(r.total_owed)], ["Remaining", usd(r.remaining_balance)]].map(([k, v]) => (
                    <div key={k} className="bg-[#0A0A0A] border border-white/10 rounded px-2 py-1.5"><span className="text-white/40">{k}</span><div className="text-white font-semibold">{v}</div></div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <select data-testid={`set-status-${r.id}`} className={inp + " !w-auto"} value={r.status} onChange={(e) => setRecStatus(r, "status", e.target.value)}>
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <select data-testid={`set-vstatus-${r.id}`} className={inp + " !w-auto"} value={r.vehicle_status} onChange={(e) => setRecStatus(r, "vehicle_status", e.target.value)}>
                    {VEHICLE_STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {(r.fee_pauses || []).length > 0 && !(r.fee_pauses[r.fee_pauses.length - 1].to) ? (
                    <>
                      <span data-testid={`fees-paused-badge-${r.id}`} className="text-[10px] uppercase font-black px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/40">Late fees PAUSED for this renter</span>
                      <button data-testid={`fee-resume-${r.id}`} onClick={async () => {
                        try { await axios.post(`${API}/leakage/${r.id}/fee-pause`, { paused: false }, auth); toast.success("Fees resumed for this renter."); load(); }
                        catch (e2) { toast.error(e2?.response?.data?.detail || "Failed."); }
                      }} className="text-xs font-bold px-3 py-1.5 rounded border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10">Resume Fees</button>
                    </>
                  ) : (
                    <button data-testid={`fee-pause-${r.id}`} onClick={async () => {
                      const reason = window.prompt(`Pause late fees for ${r.customer} only? Enter a reason (required):`, "");
                      if (reason === null) return;
                      if (!reason.trim()) { toast.error("A reason is required."); return; }
                      try { await axios.post(`${API}/leakage/${r.id}/fee-pause`, { paused: true, reason }, auth); toast.success("Fees paused for this renter — paused time is never billed."); load(); }
                      catch (e2) { toast.error(e2?.response?.data?.detail || "Failed."); }
                    }} className="text-xs font-bold px-3 py-1.5 rounded border border-amber-400/40 text-amber-300 hover:bg-amber-400/10">Pause Fees (this renter only)</button>
                  )}
                </div>
                <ClearBalance rec={r} token={token} onSaved={load} />
                <AdjustForm rec={r} token={token} onSaved={load} />
                <PromiseForm rec={r} token={token} onSaved={load} />
                {(r.promises || []).length > 0 && (
                  <div className="mt-3 text-xs space-y-1">
                    <div className="text-white/40 uppercase tracking-wide font-bold">Promises</div>
                    {r.promises.map((p, i) => (
                      <div key={i} className="flex justify-between text-white/60 border-b border-white/5 py-1">
                        <span>{usd(p.amount)} by {p.promised_date} {p.promised_time} {p.method && `(${p.method})`}</span>
                        <span className={p.outcome === "broken" ? "text-red-400 font-bold" : p.outcome === "kept" ? "text-emerald-300" : "text-amber-300"}>{p.outcome.toUpperCase()}</span>
                      </div>
                    ))}
                  </div>
                )}
                {(r.adjustments || []).length > 0 && (
                  <div className="mt-3 text-xs space-y-1">
                    <div className="text-white/40 uppercase tracking-wide font-bold">Audit Trail (permanent)</div>
                    {[...r.adjustments].reverse().map((a, i) => (
                      <div key={i} className="text-white/55 border-b border-white/5 py-1">
                        <span className="text-white/35">{fmtDateTime(a.at)}</span> · <b className="text-white/80">{a.type}</b>
                        {a.amount > 0 && <span className="text-[#F2B705]"> {usd(a.amount)}</span>} {a.applies_to !== "rental" && `(${a.applies_to})`}
                        {a.reason && ` — ${a.reason}`}{a.notes && ` · ${a.notes}`} <span className="text-white/30">by {a.by}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      )}
    </div>
  );
}

